import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCategories, fetchTerms, fetchProducts } from "../lib/api";
import { CategoryCard } from "../components/CategoryCard";
import { ProductCard } from "../components/ProductCard";
import { TermCard } from "../components/TermCard";
import { FloralOrnament } from "../components/FloralOrnament";
import { ArrowRight, Sparkles } from "lucide-react";

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [highlightedTerms, setHighlightedTerms] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [ambianceProducts, setAmbianceProducts] = useState([]);

  useEffect(() => {
    document.title = "Beckyliss Cosmetics – Glossaire Beauté";
    fetchCategories().then(setCategories);
    fetchTerms().then((t) => setHighlightedTerms(t.slice(0, 6)));
    fetchProducts().then((p) => setFeaturedProducts(p.slice(0, 8)));
    // Ambiance line: candles + Instant Cosy room fragrances
    fetchProducts({ category: "parfums" }).then((p) =>
      setAmbianceProducts(
        p.filter((x) =>
          x.sub_family === "bougie" || x.sub_family === "parfum-ambiance"
        ).slice(0, 6)
      )
    );
  }, []);

  return (
    <div className="fade-in-up" data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden hero-bg-gradient">
        <div className="absolute inset-0 -z-0">
          <div className="absolute -top-32 -right-32 w-[640px] h-[640px] rounded-full bg-bl-turquoise/30 blur-3xl" />
          <div className="absolute bottom-0 -left-24 w-[480px] h-[480px] rounded-full bg-bl-lilac/25 blur-3xl" />
        </div>
        {/* Filigree peony — top-right & bottom-left */}
        <FloralOrnament className="floral-ornament floral-ornament--tr" variant="1" testId="home-floral-tr" />
        <FloralOrnament className="floral-ornament floral-ornament--bl" variant="2" testId="home-floral-bl" />
        <div className="relative max-w-editorial mx-auto px-6 md:px-10 lg:px-14 pt-16 md:pt-24 pb-20 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight text-bl-text leading-[1.05] text-balance">
              Le glossaire <em className="italic text-bl-dore">beauté</em><br />
              de la maison <span className="italic">Beckyliss</span>.
            </h1>
            <p className="mt-8 text-base md:text-lg font-light text-bl-textSoft max-w-xl leading-relaxed">
              Une encyclopédie vivante des produits de soin, de maquillage et de parfum,
              illustrée par les créations Beckyliss. Pour comprendre, choisir et composer
              ses rituels beauté avec justesse.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/categorie/teint"
                className="inline-flex items-center gap-2 bg-bl-text text-bl-bg px-7 py-3.5 text-sm tracking-wide hover:bg-bl-dore transition-colors"
                data-testid="hero-cta-explore"
              >
                Explorer le glossaire <ArrowRight size={16} strokeWidth={1.4} />
              </Link>
              <Link
                to="/recherche"
                className="inline-flex items-center gap-2 border border-bl-text px-7 py-3.5 text-sm tracking-wide hover:bg-bl-text hover:text-bl-bg transition-colors"
                data-testid="hero-cta-search"
              >
                Rechercher un terme
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 lg:pl-10">
            <div className="relative aspect-[3/4] overflow-hidden border border-bl-border">
              <img
                src="/beckyliss-hero.jpg"
                alt="Affiche Beckyliss — Savons pour les mains"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-bl-text/80 to-transparent">
                <p className="font-serif italic text-bl-bg text-lg">
                  « Des mains douces, une conscience propre. »
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE EDITORIAL */}
      <section className="border-y border-bl-border bg-bl-soft py-6 overflow-hidden">
        <div className="marquee-track flex gap-16 whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex gap-16 items-center">
              {["Teint", "Yeux", "Lèvres", "Soins visage", "Parfums", "Cheveux", "Compléments", "Bougies", "Accessoires", "Corps"].map((label) => (
                <span key={label + k} className="font-serif italic text-2xl text-bl-text/70">
                  {label} <span className="text-bl-dore mx-6">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-24 md:py-32" data-testid="categories-section">
        <div className="grid md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-5">
            <span className="overline">Sommaire</span>
            <h2 className="font-serif text-4xl md:text-5xl text-bl-text mt-3 leading-tight">
              Dix familles, un univers.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-base text-bl-textSoft leading-relaxed">
              Du teint aux parfums, en passant par les soins du visage et les rituels capillaires,
              chaque famille rassemble les définitions clés et les produits Beckyliss qui leur
              donnent vie.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
          {categories.map((c, i) => (
            <CategoryCard key={c.slug} category={c} index={i} />
          ))}
        </div>
      </section>

      {/* TERMS HIGHLIGHTS */}
      {highlightedTerms.length > 0 && (
        <section className="bg-bl-soft py-24 md:py-32 border-y border-bl-border" data-testid="terms-highlight-section">
          <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="overline flex items-center gap-2">
                  <Sparkles size={12} strokeWidth={1.5} /> À découvrir
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-bl-text mt-3">
                  Les essentiels du glossaire
                </h2>
              </div>
              <Link
                to="/categorie/soins-visage"
                className="hidden md:inline-flex items-center gap-2 text-sm tracking-wide underline-grow"
                data-testid="terms-highlight-cta"
              >
                Tout parcourir <ArrowRight size={16} strokeWidth={1.4} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {highlightedTerms.map((t) => (
                <TermCard
                  key={t.slug}
                  term={t}
                  categoryName={t.category_slug}
                  variant="flip"
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRODUCT SHOWCASE */}
      {featuredProducts.length > 0 && (
        <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-24 md:py-32" data-testid="featured-products-section">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="overline">Sélection</span>
              <h2 className="font-serif text-4xl md:text-5xl text-bl-text mt-3">
                La maison Beckyliss
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {featuredProducts.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* AMBIANCE — Bougies & Parfums d'intérieur */}
      {ambianceProducts.length > 0 && (
        <section
          className="border-t border-bl-border bg-gradient-to-b from-transparent via-bl-rose/25 to-transparent py-24 md:py-28"
          data-testid="home-ambiance-section"
        >
          <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div className="max-w-2xl">
                <span className="overline flex items-center gap-2">
                  <Sparkles size={12} strokeWidth={1.5} />
                  Nouveautés Ambiance
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-bl-text mt-3 leading-[1.05]">
                  Le parfum <em className="italic text-bl-dore">au-delà</em> de la peau.
                </h2>
                <p className="mt-5 text-base font-light text-bl-textSoft leading-relaxed">
                  Bougies artisanales à la cire végétale et diffuseurs
                  <span className="italic"> Instant Cosy </span>
                  — pour habiller vos intérieurs d'un sillage sur-mesure.
                </p>
              </div>
              <Link
                to="/categorie/parfums"
                className="inline-flex items-center gap-2 text-sm tracking-wide border border-bl-mauveDeep text-bl-mauveDeep px-6 py-3 hover:bg-bl-mauveDeep hover:text-white transition-colors"
                data-testid="home-ambiance-cta"
              >
                Voir la ligne ambiance <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {ambianceProducts.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EDITORIAL QUOTE */}
      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="border-t border-bl-border pt-16">
          <p className="font-serif italic text-3xl md:text-5xl text-bl-text leading-tight max-w-4xl text-balance">
            « Comprendre un produit, c'est déjà commencer à se le réapproprier.
            Le glossaire Beckyliss est l'écrin pédagogique de notre univers. »
          </p>
          <p className="mt-8 overline">— Beckyliss Cosmetics</p>
        </div>
      </section>
    </div>
  );
}
